import { Check, Plus } from "lucide-react";

import { BookingScreen } from "@/features/portal/components/app-screens";
import { Phone } from "@/features/portal/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Monogram, Section } from "@/features/portal/components/portal-ui";
import { Stage } from "@/features/portal/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** The words that open `/pricing`, inside the head card. */
export const PricingHero = () => (
  <div className="relative mx-auto max-w-[900px] px-1 pt-14 pb-2 text-center md:pt-20">
    <Eyebrow>{m.portal_nav_pricing()}</Eyebrow>
    <h1 className={cn(displayTitle, "mt-6")}>
      {m.price_page_title_lead()} <Accent>{m.price_page_title_tail()}</Accent>
    </h1>
    <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
      {m.price_page_lede()}
    </p>
  </div>
);

const APP_FEATURES = [
  () => m.price_page_app_feature_plans(),
  () => m.price_page_app_feature_log(),
  () => m.price_page_app_feature_diet(),
  () => m.price_page_app_feature_recovery(),
  () => m.price_page_app_feature_booking(),
];

const VISIT_WAYS = [
  () => m.price_page_visit_single(),
  () => m.price_page_visit_pack(),
  () => m.price_page_visit_subscription(),
  () => m.price_page_visit_pay(),
];

const tick = "mt-px grid size-[20px] shrink-0 place-items-center rounded-full";

/**
 * The two prices a person meets: the app, which starts free and has a price after that, and the
 * visits, which each specialist prices themselves. Side by side, so neither is taken for the other.
 */
export const PricingCards = () => (
  <Section>
    <div className="grid gap-3 lg:grid-cols-[1.1fr_1fr]">
      <article className="relative isolate flex flex-col overflow-hidden rounded-[32px] bg-primary p-8 text-primary-foreground md:p-10">
        <span className="absolute -top-24 -right-24 -z-10 size-72 rounded-full bg-white/25 blur-3xl" />
        <p className="text-sm font-semibold tracking-[0.14em] uppercase opacity-70">{m.price_page_app_label()}</p>
        <p className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <b className="text-[clamp(3.5rem,8vw,5.5rem)] leading-none font-medium tracking-[-0.05em] whitespace-nowrap">
            {m.price_page_app_price()}
          </b>
          <span className="text-lg opacity-70">{m.price_page_app_price_note()}</span>
        </p>
        <p className="mt-4 max-w-[40ch] text-[17px] leading-[1.5] opacity-80">{m.price_page_app_body()}</p>
        <ul className="mt-8 grid flex-1 gap-3">
          {APP_FEATURES.map((feature) => (
            <li key={feature()} className="flex gap-3 text-[15px] font-medium">
              <span className={cn(tick, "bg-primary-foreground text-primary")}>
                <Check aria-hidden className="size-3" strokeWidth={3} />
              </span>
              {feature()}
            </li>
          ))}
        </ul>
        <GoLink to="/signup" className="mt-10">
          {m.portal_nav_sign_up()}
        </GoLink>
      </article>
      <article className="flex flex-col rounded-[32px] border border-border bg-background p-8 md:p-10">
        <p className="text-sm font-semibold tracking-[0.14em] text-muted-foreground uppercase">
          {m.price_page_visit_label()}
        </p>
        <p className="mt-4 text-[clamp(2.25rem,4.4vw,3.25rem)] leading-[1.02] font-medium tracking-[-0.04em] text-balance">
          {m.price_page_visit_price()}
        </p>
        <p className="mt-4 max-w-[42ch] text-[17px] leading-[1.5] text-muted-foreground">{m.price_page_visit_body()}</p>
        <ul className="mt-8 grid flex-1 gap-3">
          {VISIT_WAYS.map((way) => (
            <li key={way()} className="flex gap-3 text-[15px] font-medium">
              <span className={cn(tick, "bg-primary/15 text-[var(--site-ink)]")}>
                <Check aria-hidden className="size-3" strokeWidth={3} />
              </span>
              {way()}
            </li>
          ))}
        </ul>
        <GoLink to="/coaches" tone="lime" className="mt-10">
          {m.price_page_visit_cta()}
        </GoLink>
      </article>
    </div>
  </Section>
);

/** An entry pack as the client holds it: the sessions left, ticking down. */
const PackCard = () => (
  <div aria-hidden className="absolute inset-0 grid place-items-center pb-8">
    <div className="relative w-[min(330px,80%)]">
      <div className="absolute inset-0 translate-x-[7%] -translate-y-[14%] rotate-[7deg] rounded-[26px] bg-[linear-gradient(140deg,oklch(0.3_0.01_120),oklch(0.2_0.006_120))] ring-1 ring-white/10 ring-inset" />
      <div className="relative -rotate-[3deg] overflow-hidden rounded-[26px] bg-[linear-gradient(140deg,color-mix(in_oklch,var(--primary)_92%,white),var(--primary)_45%,color-mix(in_oklch,var(--primary)_70%,black))] p-6 text-primary-foreground shadow-[0_40px_70px_-20px_rgba(0,0,0,0.85)]">
        <span className="absolute -top-16 -right-16 size-48 rounded-full bg-white/25 blur-2xl" />
        <div className="relative flex items-center gap-3">
          <Monogram
            name={m.features_sample_coach_full()}
            className="size-10 rounded-[12px] from-primary-foreground/15 to-primary-foreground/5 text-[13px] text-primary-foreground"
          />
          <span className="leading-tight">
            <b className="block text-[15px] font-semibold">{m.features_booking_pack()}</b>
            <small className="text-[13px] opacity-70">{m.features_sample_coach_full()}</small>
          </span>
        </div>
        <p className="relative mt-6 text-[34px] leading-none font-bold tracking-[-0.03em] tabular-nums">
          {m.price_page_pack_left({ left: 6, total: 10 })}
        </p>
        <span className="relative mt-5 grid grid-cols-10 gap-1">
          {Array.from({ length: 10 }, (_, index) => (
            <i
              key={index}
              className={cn("h-2 rounded-full", index < 4 ? "bg-primary-foreground/20" : "bg-primary-foreground")}
            />
          ))}
        </span>
      </div>
    </div>
  </div>
);

/** The cancellation rule as the booking step shows it, before anything is paid. */
const PolicyCard = () => (
  <div aria-hidden className="absolute inset-0 grid place-items-center px-6 pb-8">
    <div className="w-full max-w-[360px] rounded-[26px] bg-foreground/[0.06] p-5 ring-1 ring-foreground/[0.07] ring-inset">
      <p className="text-[13px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        {m.price_page_policy_label()}
      </p>
      <ul className="mt-4 grid gap-2.5">
        {[
          { id: "free", text: m.price_page_policy_free(), tone: "bg-primary text-primary-foreground", icon: Check },
          { id: "late", text: m.price_page_policy_late(), tone: "bg-foreground/[0.1] text-foreground", icon: Plus },
        ].map((rule) => (
          <li key={rule.id} className="flex items-center gap-3 rounded-[16px] bg-background/60 p-3">
            <span className={cn("grid size-8 shrink-0 place-items-center rounded-full", rule.tone)}>
              <rule.icon className={cn("size-4", rule.id === "late" && "rotate-45")} strokeWidth={3} />
            </span>
            <span className="text-[14px] leading-snug font-medium">{rule.text}</span>
          </li>
        ))}
      </ul>
      <span className="mt-4 flex h-11 items-center justify-center rounded-full bg-foreground text-[14px] font-semibold text-background">
        {m.price_page_policy_cancel()}
      </span>
    </div>
  </div>
);

/** How paying for visits works, after the two prices: booking, packs and the cancellation rule. */
export const PricingHow = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.price_page_how_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.price_page_how_lead()} <Accent>{m.price_page_how_tail()}</Accent>
      </h2>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.price_page_book_title()}
        lead={m.price_page_book_lead()}
        body={m.price_page_book_body()}
        className="md:col-span-2"
        artClassName="flex justify-center px-6 pb-10 lg:items-center lg:py-14"
      >
        <div className="w-[min(290px,78%)] lg:w-[310px]">
          <Phone>
            <BookingScreen />
          </Phone>
        </div>
      </Stage>
      <Stage lead={m.price_page_pack_lead()} body={m.price_page_pack_body()} className="h-[560px] md:h-[600px]">
        <PackCard />
      </Stage>
      <Stage lead={m.price_page_policy_lead()} body={m.price_page_policy_body()} className="h-[560px] md:h-[600px]">
        <PolicyCard />
      </Stage>
    </div>
  </Section>
);

const QUESTIONS = [
  { id: "free", question: () => m.price_page_faq_free_q(), answer: () => m.price_page_faq_free_a() },
  { id: "who", question: () => m.price_page_faq_who_q(), answer: () => m.price_page_faq_who_a() },
  { id: "alone", question: () => m.price_page_faq_alone_q(), answer: () => m.price_page_faq_alone_a() },
  { id: "cancel", question: () => m.price_page_faq_cancel_q(), answer: () => m.price_page_faq_cancel_a() },
];

/** The questions people ask about money before they book, opened one at a time. */
export const PricingQuestions = () => (
  <Section>
    <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
      <div className="px-1">
        <Eyebrow>{m.price_page_faq_eyebrow()}</Eyebrow>
        <h2 className={cn(displayTitle, "mt-5 max-w-[12ch]")}>{m.price_page_faq_title()}</h2>
      </div>
      <div className="grid gap-2">
        {QUESTIONS.map((item) => (
          <details
            key={item.id}
            className="group rounded-[24px] border border-border bg-background px-6 open:bg-[var(--site-tint)]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-[17px] font-medium [&::-webkit-details-marker]:hidden">
              {item.question()}
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground/[0.06] transition-transform duration-200 group-open:rotate-45">
                <Plus aria-hidden className="size-4" />
              </span>
            </summary>
            <p className="max-w-[60ch] pb-6 text-[15px] leading-relaxed text-muted-foreground">{item.answer()}</p>
          </details>
        ))}
      </div>
    </div>
  </Section>
);
